import type {
  DecisionAgentModuleStatus,
  DecisionAgentSourceClass,
  GovernedToolContract,
} from './decision-agent-platform';
import type { DecisionConsoleReadModelV1 } from './decision-console';
import type { BriefTruthModelDto } from './ontology';
import type { BriefBlockReasonCode } from './brief-block-reason-codes';
import type { GateLedgerV1 } from './gate-ledger';
export {
  BRIEF_BLOCK_REASON_CODES,
  type BriefBlockReasonCode,
} from './brief-block-reason-codes';

export const BRIEFS_AGENT_TYPE = 'briefs_agent' as const;
export const BRIEFS_AGENT_MANIFEST_SCHEMA_VERSION = 'briefs_agent_manifest_v1' as const;
export const BRIEF_REQUEST_SCHEMA_VERSION = 'brief_request_v1' as const;
export const CLIENT_BRIEFING_PROFILE_SCHEMA_VERSION =
  'client_briefing_profile_v1' as const;
export const AUDIENCE_PROFILE_SCHEMA_VERSION = 'audience_profile_v1' as const;
export const BRIEF_CONTEXT_PACK_SCHEMA_VERSION = 'brief_context_pack_v1' as const;
export const EVIDENCE_BUNDLE_SCHEMA_VERSION = 'evidence_bundle_v1' as const;
export const BRIEF_SPEC_SCHEMA_VERSION = 'brief_spec_v1' as const;
export const ASK_HELIX_BRIEFS_REQUEST_SCHEMA_VERSION =
  'ask_helix_briefs_request_v1' as const;
export const ASK_HELIX_BRIEFS_RESPONSE_SCHEMA_VERSION =
  'ask_helix_briefs_response_v1' as const;

export type BriefScope = {
  scopeKind: string;
  scopeId: string;
};

export type BriefRuntimeMode = 'product' | 'preview' | 'local_test';

export type BriefNarrationMode =
  | 'cache_only'
  | 'compose_if_missing'
  | 'refresh';

export type BriefsAgentConfigPromotionStatus = 'draft' | 'promoted' | 'retired';

export type BriefsAgentConfigRef = {
  configId: string;
  version: string;
  promotionStatus: BriefsAgentConfigPromotionStatus;
};

export type BriefRequest = {
  schemaVersion: typeof BRIEF_REQUEST_SCHEMA_VERSION;
  requestId: string;
  tenantId: string;
  requestedByUserId: string;
  briefType: string;
  scope: BriefScope;
  clientProfileId: string;
  audienceProfileId: string;
  permissions: string[];
  runtimeMode: BriefRuntimeMode;
  asOf?: string;
  ownershipScope?: {
    kind: 'owner';
    ownerUserId: string;
  } | null;
};

export type AskHelixBriefsRequestDto = {
  schemaVersion?:
    | typeof ASK_HELIX_BRIEFS_REQUEST_SCHEMA_VERSION
    | typeof BRIEF_REQUEST_SCHEMA_VERSION;
  requestId?: string;
  briefType: string;
  roleId?: string;
  /**
   * Governed-narration persona overlay (`persona/{id}.json`) selected on the
   * workspace lens dropdown. Additive and fail-safe: when absent or unknown the
   * brief is composed exactly as before (no persona voice, no block).
   */
  personaOverlayId?: string | null;
  scope?: BriefScope | null;
  clientProfileId?: string | null;
  runtimeMode?: BriefRuntimeMode;
  narrationMode?: BriefNarrationMode | null;
  asOf?: string | null;
};

export type BriefVersionRef = {
  refId: string;
  version: string;
  status: 'draft' | 'approved' | 'active' | 'retired';
};

export type ClientBriefingProfile = {
  schemaVersion: typeof CLIENT_BRIEFING_PROFILE_SCHEMA_VERSION;
  profileId: string;
  tenantId: string;
  clientName: string;
  industryLanguage?: ClientBriefingIndustryLanguage;
  voiceRules?: ClientBriefingVoiceRules;
  version: string;
  status: 'draft' | 'governed' | 'active' | 'retired';
  supportedBriefTypes: string[];
  audienceProfileIds: string[];
  defaultAudienceProfileId: string;
  promptProfileRef: BriefVersionRef;
  evidencePolicyRef: BriefVersionRef;
  sourceRefs: BriefEvidenceSourceRef[];
  approval?: {
    approvedByUserId: string;
    approvedAt: string;
    decisionLedgerRef?: string;
  };
};

export type ClientBriefingIndustryLanguage = {
  industryLabel: string;
  operatingTerms: string[];
  decisionTerms: string[];
};

export type ClientBriefingVoiceRules = {
  leadWith: 'business implication and recommendation';
  recommendationStyle: 'clear point of view';
  sourceDetailPolicy: 'drill-down unless it changes the recommendation';
};

export type AudienceProfile = {
  schemaVersion: typeof AUDIENCE_PROFILE_SCHEMA_VERSION;
  audienceProfileId: string;
  tenantId?: string;
  roleId: string;
  label: string;
  version: string;
  permissionRequirements: string[];
  emphasis: BriefAudienceEmphasis[];
  voiceRules?: AudienceBriefingVoiceRules;
  sourceDetailPolicy: {
    defaultVisibility: 'drill_down';
  };
};

export type AudienceBriefingVoiceRules = {
  primaryLens: string;
  wordingBias: string[];
};

export type BriefAudienceEmphasis = {
  sectionId: string;
  weight: number;
  rationale?: string;
};

export type BriefContextPack = {
  schemaVersion: typeof BRIEF_CONTEXT_PACK_SCHEMA_VERSION;
  contextPackId: string;
  tenantId: string;
  briefType: string;
  scope: BriefScope;
  agentConfigRef?: BriefsAgentConfigRef | null;
  clientProfileId: string;
  audienceProfileId: string;
  sourceBundleIds: string[];
  evidenceBundleId: string;
  createdAt: string;
};

export type BriefEvidenceSourceClass =
  | DecisionAgentSourceClass
  | 'fixture'
  | 'demo';

export type BriefEvidenceProofLevel =
  | 'none'
  | 'source_ref'
  | 'governed_readback'
  | 'reconciled'
  | 'ledger_bound';

export type BriefEvidenceSourceRef = {
  sourceRefId: string;
  sourceClass: BriefEvidenceSourceClass;
  label: string;
  uri?: string;
  asOf: string;
};

export type EvidenceRequirementMissingBehavior = 'block_brief' | 'withhold_section';

export type EvidenceRequirement = {
  evidenceKey: string;
  label: string;
  requiredForSections: string[];
  requiredSourceClasses: BriefEvidenceSourceClass[];
  requiredPermissions?: string[];
  freshnessTtlHours: number;
  minimumConfidence: number | null;
  minimumProofLevel: BriefEvidenceProofLevel;
  missingBehavior: EvidenceRequirementMissingBehavior;
  staleBehavior: EvidenceRequirementMissingBehavior;
  conflictBehavior: EvidenceRequirementMissingBehavior;
  tenantBindingRequired: true;
  scopeBindingRequired: true;
};

export type BriefEvidenceState =
  | 'available'
  | 'missing'
  | 'stale'
  | 'conflicting'
  | 'withheld';

export type BriefEvidenceConfidenceStatus = 'scored' | 'not_scored';

export type BriefEvidenceFreshness = {
  evaluatedAt: string;
  ttlHours: number;
  status: 'fresh' | 'stale';
  expiresAt: string;
};

export type BriefEvidenceRecord = {
  evidenceKey: string;
  evidenceRefId: string;
  tenantId: string;
  scope: BriefScope;
  sourceRefId: string;
  sourceClass: BriefEvidenceSourceClass;
  asOf: string;
  confidence: number | null;
  confidenceStatus?: BriefEvidenceConfidenceStatus;
  proofLevel: BriefEvidenceProofLevel;
  state: BriefEvidenceState;
  supportsSections: string[];
  permissions: string[];
  freshness?: BriefEvidenceFreshness;
};

export type EvidenceBundle = {
  schemaVersion: typeof EVIDENCE_BUNDLE_SCHEMA_VERSION;
  evidenceBundleId: string;
  tenantId: string;
  scope: BriefScope;
  generatedAt: string;
  sourceRefs: BriefEvidenceSourceRef[];
  records: BriefEvidenceRecord[];
  truthModel?: BriefTruthModelDto | null;
  readModels?: BriefReadModels | null;
};

export type BriefReadModels = {
  decisionConsole?: DecisionConsoleReadModelV1 | null;
  watchItems?: readonly BriefWatchGeneratedItem[] | null;
};

export type AskHelixBriefsPublicTruthModelProjectionDto = Omit<
  BriefTruthModelDto['projection'],
  | 'packId'
  | 'expectedFinalVersionId'
  | 'projectedFinalVersionId'
  | 'actorUserId'
  | 'trigger'
>;

type AskHelixBriefsEmptySourceRefsDto = [];

type AskHelixBriefsPublicLinkedObjectDto<
  T extends {
    sourceRefs: unknown;
    provenance: unknown;
  },
> = Omit<T, 'id' | 'sourceRefs' | 'provenance'> & {
  sourceRefs: AskHelixBriefsEmptySourceRefsDto;
};

export type AskHelixBriefsPublicActiveStrategyDto =
  AskHelixBriefsPublicLinkedObjectDto<
    NonNullable<BriefTruthModelDto['activeStrategy']>
  >;

export type AskHelixBriefsPublicTruthModelLinkedObjectDto =
  AskHelixBriefsPublicLinkedObjectDto<BriefTruthModelDto['objectives'][number]>;

export type AskHelixBriefsPublicTruthModelAccountabilityDto =
  AskHelixBriefsPublicLinkedObjectDto<
    BriefTruthModelDto['accountableFunctionsOrOwners'][number]
  >;

export type AskHelixBriefsPublicTruthModelEvidenceRequirementDto =
  AskHelixBriefsPublicLinkedObjectDto<
    BriefTruthModelDto['evidenceRequirements'][number]
  >;

export type AskHelixBriefsPublicTruthModelGovernedStatusFieldDto = Omit<
  BriefTruthModelDto['governedStatusFields'][number],
  'subjectId' | 'sourceRefs' | 'provenance'
> & {
  sourceRefs: AskHelixBriefsEmptySourceRefsDto;
};

export type AskHelixBriefsPublicTruthModelDto = Omit<
  BriefTruthModelDto,
  | 'tenantId'
  | 'scopeId'
  | 'projection'
  | 'activeStrategy'
  | 'objectives'
  | 'designPrinciples'
  | 'constraints'
  | 'initiativesOrStrategicBets'
  | 'capabilities'
  | 'accountableFunctionsOrOwners'
  | 'evidenceRequirements'
  | 'governedStatusFields'
  | 'sourceRefs'
  | 'provenance'
  | 'unknowns'
> & {
  projection: AskHelixBriefsPublicTruthModelProjectionDto;
  activeStrategy: AskHelixBriefsPublicActiveStrategyDto | null;
  objectives: AskHelixBriefsPublicTruthModelLinkedObjectDto[];
  designPrinciples: AskHelixBriefsPublicTruthModelLinkedObjectDto[];
  constraints: AskHelixBriefsPublicTruthModelLinkedObjectDto[];
  initiativesOrStrategicBets: AskHelixBriefsPublicTruthModelLinkedObjectDto[];
  capabilities: AskHelixBriefsPublicTruthModelLinkedObjectDto[];
  accountableFunctionsOrOwners: AskHelixBriefsPublicTruthModelAccountabilityDto[];
  evidenceRequirements: AskHelixBriefsPublicTruthModelEvidenceRequirementDto[];
  governedStatusFields: AskHelixBriefsPublicTruthModelGovernedStatusFieldDto[];
  sourceRefs: AskHelixBriefsEmptySourceRefsDto;
};

export type AskHelixBriefsEvidenceBundleDto = Omit<
  EvidenceBundle,
  'truthModel' | 'readModels'
> & {
  truthModel?: AskHelixBriefsPublicTruthModelDto | null;
};

export type BriefBlockReason = {
  code: BriefBlockReasonCode;
  evidenceKey?: string;
  sectionIds: string[];
  message: string;
};

export type BriefWithheldSection = {
  sectionId: string;
  evidenceKey: string;
  reason: BriefBlockReason;
};

export type SufficiencyResult = {
  status: 'ready' | 'withheld_sections' | 'blocked';
  canProduceBrief: boolean;
  blockedReasons: BriefBlockReason[];
  withheldSections: BriefWithheldSection[];
  usableEvidenceRefs: string[];
};

export type AskHelixBriefsBlockRemediationCategory =
  | 'strategy_pack_setup'
  | 'admin_setup'
  | 'refresh_after_temporary_issue'
  | 'review_evidence_gap';

export type AskHelixBriefsBlockReasonDto = {
  message: string;
  remediationCategory: AskHelixBriefsBlockRemediationCategory;
  remediationPriority: number;
};

export type AskHelixBriefsRuntimeTraceDto = {
  schemaVersion: 'ask_helix_briefs_runtime_trace_v1';
  requestId: string;
  tenantId: string;
  userId: string;
  briefType: string;
  contextPackId: string;
  evidenceBundleId: string;
  truthModelStatus?: BriefTruthModelDto['status'] | null;
  sufficiencyStatus: SufficiencyResult['status'];
  usableEvidenceRefs: string[];
  resultStatus: 'ready' | 'blocked';
  configRef?: BriefsAgentConfigRef | null;
  synthesisCalled: boolean;
  synthesisProvider?: string | null;
  synthesisModel?: string | null;
  synthesisPromptVersion?: string | null;
  synthesisOutputSchemaName?: string | null;
  synthesisOutputSchemaStrict?: boolean;
  synthesisAttemptCount?: number;
  synthesisDurationMs?: number | null;
  blockedReasonCodes: BriefBlockReasonCode[];
  gateLedger?: GateLedgerV1;
};

export type AskHelixBriefsRunMetadataDto = {
  traceId: string;
  contextPackId: string;
  evidenceBundleId: string;
  usableEvidenceRefs: string[];
  validationStatus: 'ready' | 'blocked';
};

export type BriefSpecSection = {
  sectionId: string;
  title: string;
  body: string;
  evidenceRefIds: string[];
  withheld?: boolean;
  kind?: string | null;
  items?: BriefSpecItem[] | null;
  emptyState?: string | null;
};

export type BriefBindingSourceReadModel =
  | 'decision_console_read_model_v1'
  | 'finance_scoreboard_read_model_v1'
  | 'trend_substrate_read_model_v1'
  | 'lever_stack_read_model_v1'
  | 'drift_kernel_read_model_v1'
  | 'warning_band_read_model_v1'
  | 'gap_census_read_model_v1'
  | 'decision_docket_read_model_v1'
  | 'movement_read_model_v1'
  | 'leading_indicator_watch_read_model_v1';

export type BriefSpecBinding = {
  bindingId: string;
  label: string;
  valueLabel: string | null;
  numericValue?: number | null;
  unit?: string | null;
  sourceReadModel: BriefBindingSourceReadModel;
  sourcePath: string;
  sourceId: string;
  asOf?: string | null;
};

export type BriefSpecAskHelixLink = {
  label: string;
  question: string;
};

// EXEC-BRIEF V2 presentation extras carried on the public spec so the web renders exactly
// what the server composed (coverage honesty + lede) rather than re-deriving them.
export type BriefSpecCoverageBadge = {
  label: string;
  watching: number;
  total: number;
  askHelix?: BriefSpecAskHelixLink | null;
};

export type BriefSpecLedeRag = 'green' | 'amber' | 'red';

export type BriefSpecItemNextActionVerb = 'ask' | 'commit' | 'assign';

export type BriefSpecItemProvenance = {
  label: string;
  commitmentId?: string | null;
  sourceItemIds?: string[] | null;
  sourceIds?: string[] | null;
  evidenceRefIds?: string[] | null;
  formulaId?: string | null;
  formulaVersion?: string | null;
  transition?: 'updated' | 'pivoted' | 'entering' | 'exiting' | 'current' | null;
  watchId?: string | null;
  evidenceRefId?: string | null;
  evidenceLabel?: string | null;
  firedAt?: string | null;
  sourcePath?: string | null;
};

export type BriefWatchGeneratedItem = {
  itemId: string;
  label: string;
  governedSeries: {
    seriesId: string;
    label: string;
    movedFromLabel: string;
    movedToLabel: string;
    unit?: string | null;
    asOf?: string | null;
  };
  threshold: {
    label: string;
    crossedDirection: 'above' | 'below';
    thresholdLabel: string;
  };
  affected: {
    kind: 'commitment' | 'metric';
    id: string;
    label: string;
  };
  valueAtStakeBindingId: string;
  suggestedQuestion: string;
  nextActionVerb: BriefSpecItemNextActionVerb;
  provenance: {
    watchId: string;
    evidenceRefId: string;
    evidenceLabel?: string | null;
    firedAt?: string | null;
    sourcePath?: string | null;
  };
  rag?: BriefSpecLedeRag | null;
};

// Per-item drill-down affordance. 'inline' expands a detail card in place (same data,
// deeper cut); 'ask' fires the mapped Ask Helix question through the deep-link machinery.
// Every exec-brief item declares which it is (ASK-HELIX-CONNECTED + STORY-SURFACE laws).
export type BriefSpecItemDrillDown = {
  mode: 'inline' | 'ask';
  question?: string | null;
};

export type BriefSpecItem = {
  itemId: string;
  label: string;
  body?: string | null;
  detail?: string | null;
  posture?: string | null;
  valueLabel?: string | null;
  sourceLabel?: string | null;
  bindingIds?: string[];
  askHelix?: BriefSpecAskHelixLink | null;
  drillDown?: BriefSpecItemDrillDown | null;
  rag?: BriefSpecLedeRag | null;
  recommended?: boolean | null;
  actionId?: string | null;
  nextActionVerb?: BriefSpecItemNextActionVerb | null;
  provenance?: BriefSpecItemProvenance | null;
  /**
   * RENDER-OPEN answerability gate: when set, this item cannot be answered yet
   * (no governed source bound for its scope), so it renders as a NON-clickable
   * gap-status chip with this label instead of an Ask Helix dead-end button.
   * When present, `askHelix` is null. The product never offers a question it
   * cannot answer.
   */
  unanswerableStatus?: string | null;
};

export type BriefSpecSource = {
  label: string;
  description?: string | null;
  asOf?: string | null;
};

export type BriefSpecActivation = {
  status: 'allowed' | 'blocked';
  actionId?: string | null;
  label?: string | null;
  body?: string | null;
};

export type BriefSpec = {
  schemaVersion: typeof BRIEF_SPEC_SCHEMA_VERSION;
  briefSpecId: string;
  tenantId: string;
  briefType: string;
  scope: BriefScope;
  clientProfileId: string;
  audienceProfileId: string;
  title: string;
  headline: string;
  executiveTakeaway: string;
  sections: BriefSpecSection[];
  evidenceRefIds: string[];
  sourceDetail: {
    defaultVisibility: 'drill_down';
    sourceRefIds: string[];
  };
  sufficiency: SufficiencyResult;
  generatedAt: string;
  roleLabel?: string | null;
  takeaway?: string | null;
  recommendation?: string | null;
  activation?: BriefSpecActivation | null;
  sources?: BriefSpecSource[] | null;
  bindings?: BriefSpecBinding[] | null;
  coverageBadge?: BriefSpecCoverageBadge | null;
  ledeRag?: BriefSpecLedeRag | null;
  ledeReason?: string | null;
};

export type AskHelixBriefsPublicBriefSpecSectionDto = Omit<
  BriefSpecSection,
  'evidenceRefIds'
>;

export type AskHelixBriefsPublicBriefSpecDto = Pick<
  BriefSpec,
  | 'schemaVersion'
  | 'briefType'
  | 'title'
  | 'headline'
  | 'executiveTakeaway'
  | 'generatedAt'
> & {
  sections: AskHelixBriefsPublicBriefSpecSectionDto[];
  roleLabel?: string | null;
  takeaway?: string | null;
  recommendation?: string | null;
  activation?: BriefSpecActivation | null;
  sources?: BriefSpecSource[] | null;
  bindings?: BriefSpecBinding[] | null;
  coverageBadge?: BriefSpecCoverageBadge | null;
  ledeRag?: BriefSpecLedeRag | null;
  ledeReason?: string | null;
};

export type AskHelixBriefsResponseDto = {
  schemaVersion: typeof ASK_HELIX_BRIEFS_RESPONSE_SCHEMA_VERSION;
  responseType: 'brief_spec';
  status: 'ready' | 'blocked';
  scope?: BriefScope | null;
  briefSpec: AskHelixBriefsPublicBriefSpecDto | null;
  blockedReasons: AskHelixBriefsBlockReasonDto[];
  narrative?: {
    status: 'not_composed' | 'cached' | 'composed' | 'refreshed';
    composedAt?: string | null;
    compositionCallCount: number;
    canCompose: boolean;
    canRefresh: boolean;
  } | null;
  diagnostics?: {
    gateLedger?: GateLedgerV1 | null;
  } | null;
};

export type BriefsAgentEvalRequirements = {
  evalIds: string[];
  goldenTraceIds: string[];
  negativeEvalIds: string[];
  promotionGateIds: string[];
  requiredBeforePromotion: true;
};

export type BriefsAgentApprovalPromotionRequirements = {
  approvalRequiredBeforeActive: true;
  decisionLedgerBindingRequired: true;
  promotionGateRequired: true;
};

export type BriefsAgentManifest = {
  schemaVersion: typeof BRIEFS_AGENT_MANIFEST_SCHEMA_VERSION;
  agentType: typeof BRIEFS_AGENT_TYPE;
  agentId: string;
  displayName: string;
  version: string;
  status: DecisionAgentModuleStatus;
  supportedBriefTypes: string[];
  allowedTools: GovernedToolContract[];
  requiredEvidence: EvidenceRequirement[];
  audienceProfiles: AudienceProfile[];
  clientProfileRefs: BriefVersionRef[];
  promptProfileRefs: BriefVersionRef[];
  evalRequirements: BriefsAgentEvalRequirements;
  approvalPromotionRequirements: BriefsAgentApprovalPromotionRequirements;
};

export type BriefsAgentValidationResult = {
  valid: boolean;
  errors: string[];
};

export class BriefsAgentValidationError extends Error {
  readonly errors: string[];

  constructor(errors: string[]) {
    super(`Invalid Briefs Agent contract: ${errors.join('; ')}`);
    this.name = 'BriefsAgentValidationError';
    this.errors = errors;
  }
}

export type BriefEvidenceSufficiencyInput = {
  request: BriefRequest;
  manifest: BriefsAgentManifest;
  clientProfile: ClientBriefingProfile;
  audienceProfile: AudienceProfile;
  evidenceBundle: EvidenceBundle;
  now?: string;
};

const PRODUCT_FORBIDDEN_SOURCE_CLASSES: BriefEvidenceSourceClass[] = [
  'synthetic-demo',
  'fixture',
  'demo',
];

const CLIENT_CODEPATH_MARKERS = [
  'chrw',
  'home-depot',
  'homedepot',
  'home_depot',
  'apple',
];

const AUDIENCE_TRUTH_FIELDS = [
  'evidenceRefs',
  'sourceRefs',
  'facts',
  'metrics',
  'claims',
  'claimSupportPolicy',
  'requiredEvidence',
];

const EXECUTIVE_FORBIDDEN_TERMS = [
  'prompt',
  'llm',
  'tool call',
  'runtime',
  'schema',
  'endpoint',
  'developer',
  'provider',
  'config',
  'manifest',
  'json',
  'dto',
  'typescript',
  'api route',
  'source refs',
  'evidence checking',
  'blocked reason',
  'tenant',
  'synthetic',
  'debug',
  'validation',
  'fixture',
  'synthetic-demo',
  'workspace session',
  'action gate',
  'dashboard',
];

export function validateBriefsAgentManifest(
  manifest: BriefsAgentManifest
): BriefsAgentValidationResult {
  const errors: string[] = [];

  requireNonBlank(manifest.schemaVersion, 'schemaVersion', errors);
  if (manifest.schemaVersion !== BRIEFS_AGENT_MANIFEST_SCHEMA_VERSION) {
    errors.push(`schemaVersion must be ${BRIEFS_AGENT_MANIFEST_SCHEMA_VERSION}.`);
  }
  requireNonBlank(manifest.agentType, 'agentType', errors);
  if (manifest.agentType !== BRIEFS_AGENT_TYPE) {
    errors.push(
      'agentType must be briefs_agent; tenant or client variants belong in ClientBriefingProfile config.'
    );
  }
  requireNonBlank(manifest.agentId, 'agentId', errors);
  requireNonBlank(manifest.displayName, 'displayName', errors);
  rejectClientCodepathName(manifest.agentId, 'agentId', errors);
  rejectClientCodepathName(manifest.displayName, 'displayName', errors);

  if (!hasItems(manifest.supportedBriefTypes)) {
    errors.push('supportedBriefTypes must include at least one brief type.');
  }
  if (!hasItems(manifest.allowedTools)) {
    errors.push('allowedTools must include at least one governed tool.');
  }
  manifest.allowedTools?.forEach((tool, index) => {
    requireNonBlank(tool.toolName, `allowedTools[${index}].toolName`, errors);
    rejectClientCodepathName(tool.toolName, `allowedTools[${index}].toolName`, errors);
  });

  if (!hasItems(manifest.requiredEvidence)) {
    errors.push('requiredEvidence must include at least one evidence requirement.');
  }
  validateEvidenceRequirements(manifest.requiredEvidence ?? [], errors);

  if (!hasItems(manifest.audienceProfiles)) {
    errors.push('audienceProfiles must include at least one audience profile.');
  }
  manifest.audienceProfiles?.forEach((profile, index) => {
    collectErrors(validateAudienceProfile(profile), `audienceProfiles[${index}]`, errors);
  });

  if (!hasItems(manifest.clientProfileRefs)) {
    errors.push('clientProfileRefs must declare approved tenant profile version refs.');
  }
  validateVersionRefs(manifest.clientProfileRefs ?? [], 'clientProfileRefs', errors);
  if (!hasItems(manifest.promptProfileRefs)) {
    errors.push('promptProfileRefs must declare approved prompt/profile version refs.');
  }
  validateVersionRefs(manifest.promptProfileRefs ?? [], 'promptProfileRefs', errors);
  validateEvalRequirements(manifest.evalRequirements, errors);
  validateApprovalPromotionRequirements(manifest, errors);

  return { valid: errors.length === 0, errors };
}

export function assertValidBriefsAgentManifest(
  manifest: BriefsAgentManifest
): asserts manifest is BriefsAgentManifest {
  const result = validateBriefsAgentManifest(manifest);
  if (!result.valid) {
    throw new BriefsAgentValidationError(result.errors);
  }
}

export function validateClientBriefingProfile(
  profile: ClientBriefingProfile
): BriefsAgentValidationResult {
  const errors: string[] = [];
  requireNonBlank(profile.schemaVersion, 'schemaVersion', errors);
  if (profile.schemaVersion !== CLIENT_BRIEFING_PROFILE_SCHEMA_VERSION) {
    errors.push(`schemaVersion must be ${CLIENT_BRIEFING_PROFILE_SCHEMA_VERSION}.`);
  }
  requireNonBlank(profile.profileId, 'profileId', errors);
  requireNonBlank(profile.tenantId, 'tenantId', errors);
  requireNonBlank(profile.clientName, 'clientName', errors);
  validateClientIndustryLanguage(profile.industryLanguage, errors);
  validateClientVoiceRules(profile.voiceRules, errors);
  requireNonBlank(profile.version, 'version', errors);
  if (!hasItems(profile.supportedBriefTypes)) {
    errors.push('supportedBriefTypes must include at least one brief type.');
  }
  if (!hasItems(profile.audienceProfileIds)) {
    errors.push('audienceProfileIds must include at least one audience profile id.');
  }
  requireNonBlank(profile.defaultAudienceProfileId, 'defaultAudienceProfileId', errors);
  validateVersionRef(profile.promptProfileRef, 'promptProfileRef', errors);
  validateVersionRef(profile.evidencePolicyRef, 'evidencePolicyRef', errors);
  if (!hasItems(profile.sourceRefs)) {
    errors.push('sourceRefs must include governed source references.');
  }
  profile.sourceRefs?.forEach((sourceRef, index) => {
    validateSourceRef(sourceRef, `sourceRefs[${index}]`, errors);
  });
  if (profile.status === 'active' && isBlank(profile.approval?.approvedByUserId)) {
    errors.push('active client profiles require approval.approvedByUserId.');
  }
  if (profile.status === 'active' && isBlank(profile.approval?.approvedAt)) {
    errors.push('active client profiles require approval.approvedAt.');
  }
  return { valid: errors.length === 0, errors };
}

export function validateAudienceProfile(profile: AudienceProfile): BriefsAgentValidationResult {
  const errors: string[] = [];
  requireNonBlank(profile.schemaVersion, 'schemaVersion', errors);
  if (profile.schemaVersion !== AUDIENCE_PROFILE_SCHEMA_VERSION) {
    errors.push(`schemaVersion must be ${AUDIENCE_PROFILE_SCHEMA_VERSION}.`);
  }
  requireNonBlank(profile.audienceProfileId, 'audienceProfileId', errors);
  requireNonBlank(profile.roleId, 'roleId', errors);
  requireNonBlank(profile.label, 'label', errors);
  requireNonBlank(profile.version, 'version', errors);
  if (!hasItems(profile.permissionRequirements)) {
    errors.push('permissionRequirements must include at least one permission.');
  }
  if (!hasItems(profile.emphasis)) {
    errors.push('emphasis must include at least one section weighting.');
  }
  profile.emphasis?.forEach((emphasis, index) => {
    requireNonBlank(emphasis.sectionId, `emphasis[${index}].sectionId`, errors);
    if (!Number.isFinite(emphasis.weight) || emphasis.weight < 0 || emphasis.weight > 1) {
      errors.push(`emphasis[${index}].weight must be between 0 and 1.`);
    }
  });
  validateAudienceVoiceRules(profile.voiceRules, errors);
  if (profile.sourceDetailPolicy?.defaultVisibility !== 'drill_down') {
    errors.push('sourceDetailPolicy.defaultVisibility must be drill_down.');
  }
  const rawProfile = profile as Record<string, unknown>;
  AUDIENCE_TRUTH_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(rawProfile, field)) {
      errors.push(
        `AudienceProfile may change emphasis only; ${field} belongs in evidence/profile contracts.`
      );
    }
  });
  return { valid: errors.length === 0, errors };
}

function validateClientIndustryLanguage(
  industryLanguage: ClientBriefingIndustryLanguage | undefined,
  errors: string[],
): void {
  if (!industryLanguage) return;
  requireNonBlank(
    industryLanguage.industryLabel,
    'industryLanguage.industryLabel',
    errors,
  );
  requireNonEmptyStrings(
    industryLanguage.operatingTerms,
    'industryLanguage.operatingTerms',
    errors,
  );
  requireNonEmptyStrings(
    industryLanguage.decisionTerms,
    'industryLanguage.decisionTerms',
    errors,
  );
}

function validateClientVoiceRules(
  voiceRules: ClientBriefingVoiceRules | undefined,
  errors: string[],
): void {
  if (!voiceRules) return;
  if (voiceRules.leadWith !== 'business implication and recommendation') {
    errors.push('voiceRules.leadWith must lead with business implication and recommendation.');
  }
  if (voiceRules.recommendationStyle !== 'clear point of view') {
    errors.push('voiceRules.recommendationStyle must be clear point of view.');
  }
  if (voiceRules.sourceDetailPolicy !== 'drill-down unless it changes the recommendation') {
    errors.push(
      'voiceRules.sourceDetailPolicy must keep detail in drill-down unless it changes the recommendation.'
    );
  }
}

function validateAudienceVoiceRules(
  voiceRules: AudienceBriefingVoiceRules | undefined,
  errors: string[],
): void {
  if (!voiceRules) return;
  requireNonBlank(voiceRules.primaryLens, 'voiceRules.primaryLens', errors);
  requireNonEmptyStrings(voiceRules.wordingBias, 'voiceRules.wordingBias', errors);
}

export function validateBriefSpec(spec: BriefSpec): BriefsAgentValidationResult {
  const errors: string[] = [];
  requireNonBlank(spec.schemaVersion, 'schemaVersion', errors);
  if (spec.schemaVersion !== BRIEF_SPEC_SCHEMA_VERSION) {
    errors.push(`schemaVersion must be ${BRIEF_SPEC_SCHEMA_VERSION}.`);
  }
  requireNonBlank(spec.briefSpecId, 'briefSpecId', errors);
  requireNonBlank(spec.tenantId, 'tenantId', errors);
  requireNonBlank(spec.briefType, 'briefType', errors);
  requireNonBlank(spec.scope?.scopeKind, 'scope.scopeKind', errors);
  requireNonBlank(spec.scope?.scopeId, 'scope.scopeId', errors);
  requireNonBlank(spec.clientProfileId, 'clientProfileId', errors);
  requireNonBlank(spec.audienceProfileId, 'audienceProfileId', errors);
  validateExecutiveText(spec.title, 'title', errors);
  validateExecutiveText(spec.headline, 'headline', errors);
  validateExecutiveText(spec.executiveTakeaway, 'executiveTakeaway', errors);
  if (!hasItems(spec.sections)) {
    errors.push('sections must include at least one section.');
  }
  spec.sections?.forEach((section, index) => {
    requireNonBlank(section.sectionId, `sections[${index}].sectionId`, errors);
    validateExecutiveText(section.title, `sections[${index}].title`, errors);
    validateExecutiveText(section.body, `sections[${index}].body`, errors);
  });
  if (spec.sourceDetail?.defaultVisibility !== 'drill_down') {
    errors.push('sourceDetail.defaultVisibility must be drill_down.');
  }
  return { valid: errors.length === 0, errors };
}

export function evaluateBriefEvidenceSufficiency(
  input: BriefEvidenceSufficiencyInput
): SufficiencyResult {
  const blockedReasons: BriefBlockReason[] = [];
  const withheldSections: BriefWithheldSection[] = [];
  const usableEvidenceRefs = new Set<string>();
  const nowMs = Date.parse(input.now ?? new Date().toISOString());

  addValidationBlock(validateBriefsAgentManifest(input.manifest), 'invalid_manifest');
  addValidationBlock(validateClientBriefingProfile(input.clientProfile), 'invalid_profile');
  addValidationBlock(
    validateAudienceProfile(input.audienceProfile),
    'invalid_audience_profile'
  );

  if (input.clientProfile.tenantId !== input.request.tenantId) {
    blockedReasons.push(
      buildReason(
        'wrong_tenant',
        undefined,
        [],
        'Client profile tenant does not match request tenant.'
      )
    );
  }
  if (
    input.audienceProfile.tenantId &&
    input.audienceProfile.tenantId !== input.request.tenantId
  ) {
    blockedReasons.push(
      buildReason(
        'wrong_tenant',
        undefined,
        [],
        'Audience profile tenant does not match request tenant.'
      )
    );
  }
  if (input.evidenceBundle.tenantId !== input.request.tenantId) {
    blockedReasons.push(
      buildReason(
        'wrong_tenant',
        undefined,
        [],
        'Evidence bundle tenant does not match request tenant.'
      )
    );
  }
  if (!sameScope(input.evidenceBundle.scope, input.request.scope)) {
    blockedReasons.push(
      buildReason(
        'wrong_scope',
        undefined,
        [],
        'Evidence bundle scope does not match request scope.'
      )
    );
  }
  addPermissionBlocks(
    input.audienceProfile.permissionRequirements,
    input.request.permissions,
    'Audience profile permission requirements are not satisfied.',
    blockedReasons
  );

  (input.manifest.requiredEvidence ?? []).forEach((requirement) => {
    const matchingRecords = input.evidenceBundle.records.filter(
      (record) => record.evidenceKey === requirement.evidenceKey
    );
    if (matchingRecords.length === 0) {
      addRequirementReason(
        requirement,
        requirement.missingBehavior,
        'missing_evidence',
        'Required evidence is missing.'
      );
      return;
    }

    matchingRecords.forEach((record) => {
      const recordReasons: BriefBlockReason[] = [];
      if (record.tenantId !== input.request.tenantId) {
        recordReasons.push(
          buildReason(
            'wrong_tenant',
            requirement.evidenceKey,
            requirement.requiredForSections,
            'Evidence record tenant does not match request tenant.'
          )
        );
      }
      if (!sameScope(record.scope, input.request.scope)) {
        recordReasons.push(
          buildReason(
            'wrong_scope',
            requirement.evidenceKey,
            requirement.requiredForSections,
            'Evidence record scope does not match request scope.'
          )
        );
      }
      if (
        input.request.runtimeMode === 'product' &&
        PRODUCT_FORBIDDEN_SOURCE_CLASSES.includes(record.sourceClass)
      ) {
        recordReasons.push(
          buildReason(
            'forbidden_source_class',
            requirement.evidenceKey,
            requirement.requiredForSections,
            'Fixture, demo, or synthetic evidence cannot satisfy product runtime requirements.'
          )
        );
      }
      if (!requirement.requiredSourceClasses.includes(record.sourceClass)) {
        recordReasons.push(
          buildReason(
            'unsupported_source_class',
            requirement.evidenceKey,
            requirement.requiredForSections,
            'Evidence source class is not allowed by the requirement.'
          )
        );
      }
      // Age never blocks. The declared refresh cadence still rides on the record
      // and on the requirement so the reader is told how often this source is
      // expected to refresh — it just no longer decides whether the record is
      // usable. Only an unreadable as-of refuses, because a number rendered
      // without its as-of is a fabrication.
      if (record.state === 'stale' || isRecordUndated(record.asOf, nowMs)) {
        recordReasons.push(
          buildReason(
            'stale_evidence',
            requirement.evidenceKey,
            requirement.requiredForSections,
            'Required evidence does not carry a readable as-of date.'
          )
        );
      }
      if (record.state === 'missing') {
        recordReasons.push(
          buildReason(
            'missing_evidence',
            requirement.evidenceKey,
            requirement.requiredForSections,
            'Required evidence is marked missing.'
          )
        );
      }
      if (record.state === 'conflicting') {
        recordReasons.push(
          buildReason(
            'conflicting_evidence',
            requirement.evidenceKey,
            requirement.requiredForSections,
            'Required evidence is conflicting.'
          )
        );
      }
      if (
        requirement.minimumConfidence !== null &&
        (record.confidence === null || record.confidence < requirement.minimumConfidence)
      ) {
        recordReasons.push(
          buildReason(
            'low_confidence',
            requirement.evidenceKey,
            requirement.requiredForSections,
            'Required evidence confidence is below the minimum.'
          )
        );
      }
      if (
        proofLevelRank(record.proofLevel) < proofLevelRank(requirement.minimumProofLevel)
      ) {
        recordReasons.push(
          buildReason(
            'low_proof_level',
            requirement.evidenceKey,
            requirement.requiredForSections,
            'Required evidence proof level is below the minimum.'
          )
        );
      }
      const missingRecordPermissions = missingPermissions(
        [...(requirement.requiredPermissions ?? []), ...record.permissions],
        input.request.permissions
      );
      if (missingRecordPermissions.length > 0) {
        recordReasons.push(
          buildReason(
            'insufficient_permission',
            requirement.evidenceKey,
            requirement.requiredForSections,
            `Request lacks required evidence permissions: ${missingRecordPermissions.join(', ')}.`
          )
        );
      }

      if (recordReasons.length === 0) {
        usableEvidenceRefs.add(record.evidenceRefId);
      } else {
        recordReasons.forEach((reason) => {
          addReasonByBehavior(
            behaviorForReason(requirement, reason.code),
            requirement,
            reason
          );
        });
      }
    });
  });

  const status =
    blockedReasons.length > 0
      ? 'blocked'
      : withheldSections.length > 0
        ? 'withheld_sections'
        : 'ready';
  return {
    status,
    canProduceBrief: status !== 'blocked',
    blockedReasons,
    withheldSections,
    usableEvidenceRefs: [...usableEvidenceRefs],
  };

  function addValidationBlock(
    result: BriefsAgentValidationResult,
    code: Extract<
      BriefBlockReasonCode,
      'invalid_manifest' | 'invalid_profile' | 'invalid_audience_profile'
    >
  ): void {
    if (!result.valid) {
      blockedReasons.push(buildReason(code, undefined, [], result.errors.join('; ')));
    }
  }

  function addRequirementReason(
    requirement: EvidenceRequirement,
    behavior: EvidenceRequirementMissingBehavior,
    code: BriefBlockReasonCode,
    message: string
  ): void {
    const reason = buildReason(
      code,
      requirement.evidenceKey,
      requirement.requiredForSections,
      message
    );
    addReasonByBehavior(behavior, requirement, reason);
  }

  function addReasonByBehavior(
    behavior: EvidenceRequirementMissingBehavior,
    requirement: EvidenceRequirement,
    reason: BriefBlockReason
  ): void {
    if (behavior === 'block_brief') {
      blockedReasons.push(reason);
      return;
    }
    requirement.requiredForSections.forEach((sectionId) => {
      withheldSections.push({ sectionId, evidenceKey: requirement.evidenceKey, reason });
    });
  }
}

function validateEvidenceRequirements(
  requirements: readonly EvidenceRequirement[],
  errors: string[]
): void {
  const evidenceKeys = new Set<string>();
  requirements.forEach((requirement, index) => {
    requireNonBlank(requirement.evidenceKey, `requiredEvidence[${index}].evidenceKey`, errors);
    requireNonBlank(requirement.label, `requiredEvidence[${index}].label`, errors);
    if (evidenceKeys.has(requirement.evidenceKey)) {
      errors.push(`requiredEvidence[${index}].evidenceKey duplicates an earlier requirement.`);
    }
    evidenceKeys.add(requirement.evidenceKey);
    if (!hasItems(requirement.requiredForSections)) {
      errors.push(`requiredEvidence[${index}].requiredForSections must include at least one section.`);
    }
    if (!hasItems(requirement.requiredSourceClasses)) {
      errors.push(`requiredEvidence[${index}].requiredSourceClasses must include governed source classes.`);
    }
    requirement.requiredSourceClasses?.forEach((sourceClass) => {
      if (PRODUCT_FORBIDDEN_SOURCE_CLASSES.includes(sourceClass)) {
        errors.push(
          `requiredEvidence[${index}].requiredSourceClasses cannot include fixture, demo, or synthetic-demo.`
        );
      }
    });
    if (!Number.isFinite(requirement.freshnessTtlHours) || requirement.freshnessTtlHours <= 0) {
      errors.push(`requiredEvidence[${index}].freshnessTtlHours must be greater than zero.`);
    }
    if (
      requirement.minimumConfidence !== null &&
      (!Number.isFinite(requirement.minimumConfidence) ||
        requirement.minimumConfidence < 0 ||
        requirement.minimumConfidence > 1)
    ) {
      errors.push(`requiredEvidence[${index}].minimumConfidence must be between 0 and 1.`);
    }
    if (proofLevelRank(requirement.minimumProofLevel) < 0) {
      errors.push(`requiredEvidence[${index}].minimumProofLevel is not supported.`);
    }
    if (requirement.tenantBindingRequired !== true) {
      errors.push(`requiredEvidence[${index}].tenantBindingRequired must be true.`);
    }
    if (requirement.scopeBindingRequired !== true) {
      errors.push(`requiredEvidence[${index}].scopeBindingRequired must be true.`);
    }
  });
}

function validateVersionRefs(
  refs: readonly BriefVersionRef[],
  fieldName: string,
  errors: string[]
): void {
  refs.forEach((ref, index) => {
    validateVersionRef(ref, `${fieldName}[${index}]`, errors);
  });
}

function validateVersionRef(
  ref: BriefVersionRef | undefined,
  fieldName: string,
  errors: string[]
): void {
  requireNonBlank(ref?.refId, `${fieldName}.refId`, errors);
  requireNonBlank(ref?.version, `${fieldName}.version`, errors);
  if (!ref?.status || !['draft', 'approved', 'active', 'retired'].includes(ref.status)) {
    errors.push(`${fieldName}.status must be draft, approved, active, or retired.`);
  }
}

function validateSourceRef(
  sourceRef: BriefEvidenceSourceRef,
  fieldName: string,
  errors: string[]
): void {
  requireNonBlank(sourceRef.sourceRefId, `${fieldName}.sourceRefId`, errors);
  requireNonBlank(sourceRef.sourceClass, `${fieldName}.sourceClass`, errors);
  requireNonBlank(sourceRef.label, `${fieldName}.label`, errors);
  requireNonBlank(sourceRef.asOf, `${fieldName}.asOf`, errors);
  if (PRODUCT_FORBIDDEN_SOURCE_CLASSES.includes(sourceRef.sourceClass)) {
    errors.push(`${fieldName}.sourceClass cannot be fixture, demo, or synthetic-demo.`);
  }
}

function validateEvalRequirements(
  evalRequirements: BriefsAgentEvalRequirements | undefined,
  errors: string[]
): void {
  if (!evalRequirements) {
    errors.push('evalRequirements is required.');
    return;
  }
  if (!hasItems(evalRequirements.evalIds)) {
    errors.push('evalRequirements.evalIds must include at least one eval id.');
  }
  if (!hasItems(evalRequirements.goldenTraceIds)) {
    errors.push('evalRequirements.goldenTraceIds must include at least one golden trace id.');
  }
  if (!hasItems(evalRequirements.negativeEvalIds)) {
    errors.push('evalRequirements.negativeEvalIds must include at least one negative eval id.');
  }
  if (!hasItems(evalRequirements.promotionGateIds)) {
    errors.push('evalRequirements.promotionGateIds must include at least one promotion gate id.');
  }
  if (evalRequirements.requiredBeforePromotion !== true) {
    errors.push('evalRequirements.requiredBeforePromotion must be true.');
  }
}

function validateApprovalPromotionRequirements(
  manifest: BriefsAgentManifest,
  errors: string[]
): void {
  const requirements = manifest.approvalPromotionRequirements;
  if (!requirements) {
    errors.push('approvalPromotionRequirements is required.');
    return;
  }
  if (requirements.approvalRequiredBeforeActive !== true) {
    errors.push('approvalPromotionRequirements.approvalRequiredBeforeActive must be true.');
  }
  if (requirements.decisionLedgerBindingRequired !== true) {
    errors.push('approvalPromotionRequirements.decisionLedgerBindingRequired must be true.');
  }
  if (requirements.promotionGateRequired !== true) {
    errors.push('approvalPromotionRequirements.promotionGateRequired must be true.');
  }
  if (
    manifest.status === 'active' &&
    (!requirements.approvalRequiredBeforeActive ||
      !requirements.decisionLedgerBindingRequired ||
      !requirements.promotionGateRequired)
  ) {
    errors.push('active Briefs Agent manifests require approval, Decision Ledger binding, and promotion gate requirements.');
  }
}

function validateExecutiveText(
  value: string | undefined,
  fieldName: string,
  errors: string[]
): void {
  requireNonBlank(value, fieldName, errors);
  const normalized = (value ?? '').toLowerCase();
  EXECUTIVE_FORBIDDEN_TERMS.forEach((term) => {
    if (normalized.includes(term)) {
      errors.push(`${fieldName} contains internal/developer vocabulary: ${term}.`);
    }
  });
}

function addPermissionBlocks(
  requiredPermissions: readonly string[],
  actualPermissions: readonly string[],
  message: string,
  blockedReasons: BriefBlockReason[]
): void {
  const missing = missingPermissions(requiredPermissions, actualPermissions);
  if (missing.length > 0) {
    blockedReasons.push(
      buildReason(
        'insufficient_permission',
        undefined,
        [],
        `${message} Missing permissions: ${missing.join(', ')}.`
      )
    );
  }
}

function missingPermissions(
  requiredPermissions: readonly string[],
  actualPermissions: readonly string[]
): string[] {
  const actual = new Set(actualPermissions);
  return [...new Set(requiredPermissions)].filter((permission) => !actual.has(permission));
}

function behaviorForReason(
  requirement: EvidenceRequirement,
  code: BriefBlockReasonCode
): EvidenceRequirementMissingBehavior {
  if (code === 'stale_evidence') {
    return requirement.staleBehavior;
  }
  if (code === 'conflicting_evidence') {
    return requirement.conflictBehavior;
  }
  if (code === 'missing_evidence') {
    return requirement.missingBehavior;
  }
  return 'block_brief';
}

function rejectClientCodepathName(value: string | undefined, fieldName: string, errors: string[]): void {
  const normalized = (value ?? '').toLowerCase();
  CLIENT_CODEPATH_MARKERS.forEach((marker) => {
    if (normalized.includes(marker)) {
      errors.push(`${fieldName} must not encode a tenant/client-specific Briefs Agent codepath.`);
    }
  });
}

function collectErrors(
  result: BriefsAgentValidationResult,
  fieldName: string,
  errors: string[]
): void {
  result.errors.forEach((error) => errors.push(`${fieldName}.${error}`));
}

function buildReason(
  code: BriefBlockReasonCode,
  evidenceKey: string | undefined,
  sectionIds: string[],
  message: string
): BriefBlockReason {
  return evidenceKey
    ? { code, evidenceKey, sectionIds, message }
    : { code, sectionIds, message };
}

function sameScope(left: BriefScope | undefined, right: BriefScope | undefined): boolean {
  return (
    left?.scopeKind === right?.scopeKind &&
    left?.scopeId === right?.scopeId
  );
}

/**
 * Age never blocks.
 *
 * This used to be `isRecordStale`, and it returned true for any record older
 * than its declared refresh window — which dropped the record from
 * `usableEvidenceRefs` and, under `staleBehavior: 'withhold_section'`, deleted
 * every section it backed. It is the last gate in the brief path, so converting
 * the two providers without converting this would have changed nothing.
 *
 * What remains is the part that was never about age: a record whose as-of we
 * cannot read. A number rendered without its as-of is a fabrication, so an
 * undated record is still refused — with the same reason code and the same
 * downstream behaviour it has always produced for that case.
 */
function isRecordUndated(asOf: string, nowMs: number): boolean {
  return !Number.isFinite(Date.parse(asOf)) || !Number.isFinite(nowMs);
}

function proofLevelRank(proofLevel: BriefEvidenceProofLevel | undefined): number {
  const ranks: Record<BriefEvidenceProofLevel, number> = {
    none: 0,
    source_ref: 1,
    governed_readback: 2,
    reconciled: 3,
    ledger_bound: 4,
  };
  return proofLevel ? ranks[proofLevel] ?? -1 : -1;
}

function requireNonBlank(value: string | undefined, fieldName: string, errors: string[]): void {
  if (isBlank(value)) {
    errors.push(`${fieldName} is required.`);
  }
}

function requireNonEmptyStrings(
  values: readonly string[] | undefined,
  fieldName: string,
  errors: string[],
): void {
  if (!hasItems(values) || values.some((value) => isBlank(value))) {
    errors.push(`${fieldName} must include at least one non-empty value.`);
  }
}

function hasItems<T>(items: readonly T[] | undefined): items is readonly T[] {
  return Array.isArray(items) && items.length > 0;
}

function isBlank(value: string | undefined): boolean {
  return typeof value !== 'string' || value.trim().length === 0;
}
